/**
 * TonkLobby — browse open Tonk tables + create / join by code.
 *
 * REST polling every 3s for the table list. Live gameplay uses WebSockets
 * inside TonkRoom. Matches the Street Dice aesthetic (dark canvas + gold
 * accent) but uses a green felt palette to signal "cards" vs. "dice".
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
    ArrowLeft, Spade, Users, Bot, Plus, RefreshCw, Sparkles,
    Shield, AlertTriangle, HelpCircle, X, Lock, PlayCircle,
} from 'lucide-react';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const HOUSE_RULES_KEY = 'tonk-rules-seen-v1';

// Green felt theme — tonk gameplay signature
const THEME = {
    accent: '#F5C542',        // gold chip
    felt: '#0E4A2A',          // classic card-table green
    pageBg: 'radial-gradient(ellipse at 50% -10%, #0E4A2A 0%, #052012 55%, #030B08 100%)',
    circleBg: 'radial-gradient(ellipse at 50% 20%, rgba(14,74,42,0.75) 0%, rgba(3,11,8,0.9) 70%)',
    glow: 'rgba(245,197,66,0.35)',
    headingFont: '"Bebas Neue", "Oswald", Impact, sans-serif',
    headingLetter: '0.03em',
};

const fmt$ = (n) =>
    `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const PHASE_LABEL = {
    waiting: 'Open — waiting to deal',
    dealing: 'Dealing',
    in_hand: 'Hand in progress',
    resolving: 'Resolving',
    hand_ended: 'Between hands',
    closed: 'Closed',
};

export default function TonkLobby() {
    const nav = useNavigate();
    const { user } = useAuth();
    const [tables, setTables] = useState([]);
    const [myActiveTables, setMyActiveTables] = useState([]);
    const [loading, setLoading] = useState(true);
    const [createOpen, setCreateOpen] = useState(false);
    const [joinCodeOpen, setJoinCodeOpen] = useState(false);
    const [rulesOpen, setRulesOpen] = useState(false);

    // First-visit rules modal
    useEffect(() => {
        try {
            if (!localStorage.getItem(HOUSE_RULES_KEY)) {
                const t = setTimeout(() => setRulesOpen(true), 600);
                return () => clearTimeout(t);
            }
        } catch { /* ignore */ }
    }, []);

    const load = useCallback(async () => {
        try {
            const [{ data: listData }, { data: mineData }] = await Promise.all([
                axios.get(`${API}/tonk/tables`, { withCredentials: true }),
                axios.get(`${API}/tonk/my-active-tables`, { withCredentials: true })
                    .catch(() => ({ data: { tables: [] } })),
            ]);
            setTables(listData.tables || listData || []);
            setMyActiveTables(mineData?.tables || []);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            if (detail) toast.error(detail);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
        const id = setInterval(load, 3000);
        return () => clearInterval(id);
    }, [load]);

    const openTables = useMemo(
        () => tables.filter((t) => t.phase !== 'closed'),
        [tables],
    );

    return (
        <div className="a11y-font min-h-screen text-white relative overflow-hidden" style={{ background: THEME.pageBg }} data-testid="tonk-lobby">
            <Header />
            <div className="relative max-w-5xl mx-auto px-4 pt-6 pb-24">
                <button
                    onClick={() => nav('/lobby')}
                    className="inline-flex items-center gap-1 text-sm text-white/70 hover:text-white mb-4"
                    data-testid="tonk-back-link"
                >
                    <ArrowLeft className="w-4 h-4" /> Back to lobby
                </button>

                {/* Resume-your-table banner — if the user has any tables where
                    they still hold a seat, surface them prominently so they
                    can jump right back in without recreating. */}
                {myActiveTables.length > 0 && (
                    <motion.div
                        initial={{ opacity: 0, y: -6 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="rounded-2xl border-2 p-3 mb-4 flex items-center gap-3 flex-wrap"
                        style={{
                            borderColor: THEME.accent,
                            background: 'linear-gradient(90deg, rgba(245,197,66,0.14) 0%, rgba(14,74,42,0.4) 100%)',
                            boxShadow: `0 0 22px ${THEME.glow}`,
                        }}
                        data-testid="tonk-resume-banner"
                    >
                        <div className="flex items-center gap-2 shrink-0">
                            <PlayCircle className="w-5 h-5" style={{ color: THEME.accent }} />
                            <span
                                className="text-xs font-black uppercase tracking-widest"
                                style={{ color: THEME.accent, fontFamily: THEME.headingFont, letterSpacing: '0.14em' }}
                            >
                                {myActiveTables.length === 1 ? 'Resume your table' : `Resume (${myActiveTables.length})`}
                            </span>
                        </div>
                        <div className="flex flex-wrap gap-2 flex-1">
                            {myActiveTables.slice(0, 4).map((t) => (
                                <button
                                    key={t.id}
                                    type="button"
                                    onClick={() => nav(`/casino/tonk/${t.id}`)}
                                    className="flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-bold transition hover:brightness-110"
                                    style={{
                                        borderColor: THEME.accent,
                                        background: 'rgba(3,11,8,0.7)',
                                        color: '#fff',
                                    }}
                                    data-testid={`tonk-resume-${t.id}`}
                                    title={`Hand #${t.hand_number} · seat ${t.seat_index + 1}`}
                                >
                                    <span className="truncate max-w-[140px]">{t.name || 'Table'}</span>
                                    <span className="text-white/50">·</span>
                                    <span className="text-[10px] font-mono text-white/70">
                                        {PHASE_LABEL[t.phase] || t.phase}
                                    </span>
                                    <span className="text-[10px] font-mono" style={{ color: THEME.accent }}>
                                        #{t.hand_number}
                                    </span>
                                </button>
                            ))}
                        </div>
                    </motion.div>
                )}

                {/* Hero */}
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="rounded-2xl overflow-hidden border p-6 sm:p-8 mb-6 relative"
                    style={{
                        borderColor: `${THEME.accent}66`,
                        background: THEME.circleBg,
                        boxShadow: `0 0 40px ${THEME.glow}, inset 0 0 60px rgba(0,0,0,0.6)`,
                    }}
                >
                    <div className="flex items-center gap-2 mb-2">
                        <Sparkles className="w-4 h-4" style={{ color: THEME.accent }} />
                        <span
                            className="text-[10px] font-black tracking-[0.3em]"
                            style={{ color: THEME.accent, fontFamily: THEME.headingFont, letterSpacing: THEME.headingLetter }}
                        >
                            GOOD OL&apos; TONK
                        </span>
                    </div>
                    <h1
                        className="text-4xl sm:text-6xl font-black text-white leading-tight"
                        style={{ fontFamily: THEME.headingFont, letterSpacing: THEME.headingLetter }}
                    >
                        RUN THE TABLE
                    </h1>
                    <p className="text-sm text-white/70 mt-2 max-w-xl">
                        2–5 players (human + bots) · 5-card hands · knock, drop, spread & catch ·
                        20-second turns · real wallet or practice.
                    </p>

                    <div className="flex flex-wrap gap-2 mt-5">
                        <Button
                            onClick={() => setCreateOpen(true)}
                            className="font-black h-11 px-5 text-sm tracking-wider text-black"
                            style={{ background: THEME.accent }}
                            data-testid="tonk-create-btn"
                        >
                            <Plus className="w-4 h-4 mr-1" /> NEW TABLE
                        </Button>
                        <Button
                            onClick={() => setJoinCodeOpen(true)}
                            variant="outline"
                            className="h-11 px-4 border-white/20 text-white/80 hover:text-white hover:bg-white/5"
                            data-testid="tonk-join-code-btn"
                        >
                            <Lock className="w-4 h-4 mr-1" /> Join by code
                        </Button>
                        <Button
                            onClick={() => setRulesOpen(true)}
                            variant="outline"
                            className="h-11 px-4 border-white/20 text-white/80 hover:text-white hover:bg-white/5"
                            data-testid="tonk-rules-btn"
                        >
                            <HelpCircle className="w-4 h-4 mr-1" /> How to play
                        </Button>
                        <Button
                            onClick={load}
                            variant="ghost"
                            className="h-11 px-3 text-white/60 hover:text-white"
                            data-testid="tonk-refresh-btn"
                        >
                            <RefreshCw className="w-4 h-4" />
                        </Button>
                    </div>
                </motion.div>

                {/* Table list */}
                <div className="flex items-center justify-between mb-3">
                    <h2
                        className="text-sm tracking-[0.28em] font-black text-white/80"
                        style={{ fontFamily: THEME.headingFont }}
                    >
                        OPEN TABLES ({openTables.length})
                    </h2>
                    <span className="text-[10px] font-mono text-white/40">Refreshing every 3s</span>
                </div>

                {loading ? (
                    <div className="text-white/50 text-sm py-8 text-center" data-testid="tonk-loading">
                        Loading tables…
                    </div>
                ) : openTables.length === 0 ? (
                    <div
                        className="rounded-2xl border border-dashed p-8 text-center"
                        style={{ borderColor: `${THEME.accent}44`, background: 'rgba(0,0,0,0.3)' }}
                        data-testid="tonk-empty"
                    >
                        <Spade className="w-10 h-10 mx-auto mb-3" style={{ color: THEME.accent }} />
                        <p className="text-white/80 font-bold">No tables running yet.</p>
                        <p className="text-white/50 text-xs mt-1 mb-4">Start one and the bots will pull up a seat.</p>
                        <Button
                            onClick={() => setCreateOpen(true)}
                            className="font-black text-black"
                            style={{ background: THEME.accent }}
                            data-testid="tonk-create-btn-empty"
                        >
                            <Plus className="w-4 h-4 mr-1" /> DEAL ME IN
                        </Button>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {openTables.map((t) => (
                            <TableCard
                                key={t.id}
                                table={t}
                                onOpen={() => nav(`/casino/tonk/${t.id}`)}
                                mine={t.seats?.some((s) => s.user_id === user?.id)}
                            />
                        ))}
                    </div>
                )}

                <div className="mt-6 flex items-start gap-2 rounded-xl border border-white/10 bg-black/40 p-3">
                    <Shield className="w-4 h-4 text-white/60 mt-0.5 shrink-0" />
                    <p className="text-[11px] text-white/60 leading-snug">
                        Cards are shuffled on our server. Bots back-fill empty seats so tables
                        can start with 1 human + N bots — or two humans can play heads-up with
                        no bots. Real-money tables debit / credit your BETohalics wallet
                        instantly on every hand.
                    </p>
                </div>
            </div>

            <CreateTableDialog
                open={createOpen}
                onClose={() => setCreateOpen(false)}
                onCreated={(table) => {
                    setCreateOpen(false);
                    nav(`/casino/tonk/${table.id}`);
                }}
            />
            <JoinByCodeDialog
                open={joinCodeOpen}
                onClose={() => setJoinCodeOpen(false)}
                onResolved={(t) => {
                    setJoinCodeOpen(false);
                    nav(`/casino/tonk/${t.id}?code=${encodeURIComponent(t.join_code || '')}`);
                }}
            />
            <HouseRulesDialog
                open={rulesOpen}
                onClose={() => {
                    setRulesOpen(false);
                    try { localStorage.setItem(HOUSE_RULES_KEY, '1'); } catch { /* ignore */ }
                }}
            />
        </div>
    );
}

// ─── Join by code dialog ───────────────────────────────────────────────
const JoinByCodeDialog = ({ open, onClose, onResolved }) => {
    const [code, setCode] = useState('');
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!open) { setCode(''); setBusy(false); }
    }, [open]);

    const submit = async (e) => {
        e?.preventDefault?.();
        const c = code.trim().toUpperCase();
        if (c.length < 4) return toast.error('Enter the join code');
        setBusy(true);
        try {
            const { data } = await axios.post(
                `${API}/tonk/tables/lookup`,
                { join_code: c },
                { withCredentials: true },
            );
            onResolved({ ...data, join_code: c });
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Invalid code');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="bg-[#0B0B0B] text-white border" style={{ borderColor: `${THEME.accent}55` }}>
                <DialogHeader>
                    <DialogTitle
                        className="text-white"
                        style={{ fontFamily: THEME.headingFont, letterSpacing: THEME.headingLetter }}
                    >
                        Join a private table
                    </DialogTitle>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-3">
                    <Label className="text-xs text-white/70">Join code</Label>
                    <Input
                        value={code}
                        onChange={(e) => setCode(e.target.value.toUpperCase())}
                        placeholder="A1B2C3"
                        maxLength={12}
                        autoFocus
                        className="bg-black/40 border-white/10 text-white text-2xl h-14 font-black tracking-[0.4em] text-center"
                        data-testid="tonk-join-code-input"
                    />
                    <div className="flex gap-2">
                        <Button type="button" variant="ghost" onClick={onClose} className="flex-1 text-white/70" disabled={busy}>Cancel</Button>
                        <Button
                            type="submit"
                            disabled={busy || !code.trim()}
                            className="flex-1 font-black text-black"
                            style={{ background: THEME.accent }}
                            data-testid="tonk-join-code-submit"
                        >
                            {busy ? 'Looking…' : 'FIND TABLE'}
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
};

// ─── Table card ────────────────────────────────────────────────────────
const TableCard = ({ table, onOpen, mine }) => {
    const humans = table.seats?.filter((s) => s.user_id && !s.is_bot).length || 0;
    const bots = table.seats?.filter((s) => s.is_bot).length || 0;
    const total = humans + bots;
    const max = table.rules?.max_players || table.seats?.length || 5;
    const isReal = table.mode === 'real';
    const entry = table.rules?.entry_amount ?? 5;
    return (
        <button
            onClick={onOpen}
            className="text-left rounded-2xl border-2 p-4 transition hover:-translate-y-0.5 relative overflow-hidden"
            style={{
                borderColor: mine ? THEME.accent : `${THEME.accent}44`,
                background: THEME.circleBg,
                boxShadow: mine ? `0 0 22px ${THEME.glow}` : 'inset 0 0 30px rgba(0,0,0,0.5)',
            }}
            data-testid={`tonk-table-card-${table.id}`}
        >
            <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0">
                    <div
                        className="text-lg font-black text-white truncate"
                        style={{ fontFamily: THEME.headingFont, letterSpacing: THEME.headingLetter }}
                    >
                        {table.name}
                    </div>
                    <div className="text-[11px] font-mono text-white/60 mt-0.5 truncate">
                        {PHASE_LABEL[table.phase] || table.phase} · Hand #{table.hand_number || 0}
                    </div>
                </div>
                <span
                    className={`px-2 py-0.5 rounded-full text-[9px] font-black tracking-widest ${isReal ? 'text-black' : 'text-white/80'}`}
                    style={{
                        background: isReal ? THEME.accent : 'rgba(255,255,255,0.1)',
                        border: isReal ? 'none' : '1px solid rgba(255,255,255,0.15)',
                    }}
                >
                    {isReal ? 'REAL $' : 'PRACTICE'}
                </span>
            </div>

            <div className="flex items-center justify-between mt-3">
                <div className="flex items-center gap-3 text-xs text-white/70">
                    <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" />{humans}</span>
                    <span className="flex items-center gap-1"><Bot className="w-3.5 h-3.5" />{bots}</span>
                    <span className="text-white/40">·</span>
                    <span className="font-mono">{total}/{max}</span>
                </div>
                <div className="text-[10px] font-mono text-white/60">
                    Ante <span className="text-white">{fmt$(entry)}</span>
                </div>
            </div>

            {mine && (
                <div
                    className="absolute top-2 right-2 text-[9px] font-black px-1.5 py-0.5 rounded"
                    style={{ background: THEME.accent, color: '#000' }}
                >
                    SEATED
                </div>
            )}
        </button>
    );
};

// ─── Create table dialog ───────────────────────────────────────────────
const CreateTableDialog = ({ open, onClose, onCreated }) => {
    const { user } = useAuth();
    const playableWallet = Math.max(
        0,
        Number(user?.credits ?? 0) + Number(user?.bonus_credits ?? 0),
    );
    const [name, setName] = useState('');
    const [mode, setMode] = useState('practice');
    const [maxPlayers, setMaxPlayers] = useState('4');
    const [entryAmount, setEntryAmount] = useState('5');
    const [fillWithBots, setFillWithBots] = useState(true);
    const [isPrivate, setIsPrivate] = useState(false);
    const [customCode, setCustomCode] = useState('');
    const [busy, setBusy] = useState(false);
    const [flags, setFlags] = useState({ real_money_enabled: false, min_entry: 1, max_entry: 100 });

    useEffect(() => {
        if (!open) return;
        (async () => {
            try {
                const { data } = await axios.get(`${API}/tonk/public-settings`, { withCredentials: true });
                setFlags(data || {});
            } catch { /* ignore */ }
        })();
    }, [open]);

    useEffect(() => {
        if (!open) {
            setName(''); setMode('practice'); setMaxPlayers('4');
            setEntryAmount('5'); setFillWithBots(true);
            setIsPrivate(false); setCustomCode(''); setBusy(false);
        }
    }, [open]);

    const submit = async () => {
        if (!name.trim()) return toast.error('Give the table a name');
        const entry = parseFloat(entryAmount);
        if (!entry || entry <= 0) return toast.error('Ante must be positive');
        if (mode === 'real' && entry > playableWallet) {
            return toast.error(`Your wallet only has ${fmt$(playableWallet)} — pick a lower ante`);
        }
        const mp = parseInt(maxPlayers, 10);
        if (mp < 2 || mp > 4) return toast.error('Max players must be 2–4 (one per side of the felt)');
        let submitCode = null;
        if (isPrivate && customCode.trim()) {
            const c = customCode.trim().toUpperCase();
            if (!/^[A-Z0-9]{4,12}$/.test(c)) {
                return toast.error('Custom code must be 4–12 letters/numbers');
            }
            submitCode = c;
        }
        setBusy(true);
        try {
            const { data } = await axios.post(`${API}/tonk/tables`, {
                name: name.trim(),
                mode,
                max_players: mp,
                entry_amount: entry,
                fill_with_bots: fillWithBots,
                is_private: isPrivate,
                ...(submitCode ? { join_code: submitCode } : {}),
            }, { withCredentials: true });
            onCreated(data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Could not create table');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="bg-[#0B0B0B] text-white border" style={{ borderColor: `${THEME.accent}55` }}>
                <DialogHeader>
                    <DialogTitle
                        className="text-white"
                        style={{ fontFamily: THEME.headingFont, letterSpacing: THEME.headingLetter }}
                    >
                        Deal a new table
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label className="text-xs text-white/70">Table name</Label>
                        <Input
                            value={name} onChange={(e) => setName(e.target.value)}
                            placeholder="Corner Booth"
                            className="bg-black/40 border-white/10 text-white mt-1"
                            data-testid="tonk-create-name"
                            maxLength={40}
                        />
                    </div>

                    <div>
                        <Label className="text-xs text-white/70">Mode</Label>
                        <div className="grid grid-cols-2 gap-2 mt-1">
                            <button
                                type="button"
                                onClick={() => setMode('practice')}
                                className={`rounded-lg border py-2 text-xs font-black tracking-widest ${mode === 'practice' ? 'text-black' : 'text-white/70'}`}
                                style={{
                                    background: mode === 'practice' ? THEME.accent : 'transparent',
                                    borderColor: mode === 'practice' ? THEME.accent : 'rgba(255,255,255,0.15)',
                                }}
                                data-testid="tonk-create-mode-practice"
                            >
                                PRACTICE
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    if (!flags.real_money_enabled) {
                                        toast.error('Real-money Tonk is disabled. Ask an admin to enable.');
                                        return;
                                    }
                                    setMode('real');
                                }}
                                className={`rounded-lg border py-2 text-xs font-black tracking-widest ${mode === 'real' ? 'text-black' : 'text-white/70'} ${!flags.real_money_enabled ? 'opacity-50' : ''}`}
                                style={{
                                    background: mode === 'real' ? '#FFD700' : 'transparent',
                                    borderColor: mode === 'real' ? '#FFD700' : 'rgba(255,255,255,0.15)',
                                }}
                                data-testid="tonk-create-mode-real"
                            >
                                REAL $
                            </button>
                        </div>
                        {mode === 'real' && (
                            <div className="mt-2 flex items-start gap-2 rounded-lg p-2 border border-[#FFD700]/40 bg-[#FFD700]/10">
                                <AlertTriangle className="w-4 h-4 text-[#FFD700] mt-0.5 shrink-0" />
                                <p className="text-[11px] text-white/85 leading-snug">
                                    Real money: your ante is deducted from your wallet each hand. Wins land back instantly.
                                    Wallet: <span className="text-[#FFD700] font-bold">{fmt$(playableWallet)}</span>
                                </p>
                            </div>
                        )}
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <Label className="text-xs text-white/70">Max players (2–4)</Label>
                            <Input
                                type="number" min="2" max="4" value={maxPlayers}
                                onChange={(e) => setMaxPlayers(e.target.value)}
                                className="bg-black/40 border-white/10 text-white mt-1"
                                data-testid="tonk-create-maxplayers"
                            />
                        </div>
                        <div>
                            <Label className="text-xs text-white/70">Ante per hand</Label>
                            <Input
                                type="number" min="1" value={entryAmount}
                                onChange={(e) => setEntryAmount(e.target.value)}
                                className="bg-black/40 border-white/10 text-white mt-1"
                                data-testid="tonk-create-entry"
                            />
                        </div>
                    </div>

                    <div className="flex items-center justify-between rounded-lg border border-white/10 bg-black/30 p-3">
                        <div>
                            <p className="text-sm font-bold text-white">Fill with bots</p>
                            <p className="text-[11px] text-white/50">Back-fills empty seats so the deal can start</p>
                        </div>
                        <Switch
                            checked={fillWithBots}
                            onCheckedChange={setFillWithBots}
                            data-testid="tonk-create-bots"
                        />
                    </div>

                    <div className="flex items-center justify-between rounded-lg border border-white/10 bg-black/30 p-3">
                        <div>
                            <p className="text-sm font-bold text-white">Private table</p>
                            <p className="text-[11px] text-white/50">Hidden from lobby. Share a code to invite.</p>
                        </div>
                        <Switch
                            checked={isPrivate}
                            onCheckedChange={setIsPrivate}
                            data-testid="tonk-create-private"
                        />
                    </div>

                    {isPrivate && (
                        <div className="rounded-lg border border-white/10 bg-black/30 p-3 space-y-2">
                            <div className="flex items-center justify-between">
                                <p className="text-sm font-bold text-white">Custom join code</p>
                                <span className="text-[10px] text-white/50">optional</span>
                            </div>
                            <Input
                                value={customCode}
                                onChange={(e) => setCustomCode(e.target.value.toUpperCase())}
                                placeholder="e.g. TONKGANG"
                                maxLength={12}
                                className="bg-black/50 border-white/10 text-white text-lg font-black tracking-[0.35em] text-center"
                                data-testid="tonk-create-custom-code"
                            />
                            <p className="text-[11px] text-white/50">
                                4–12 letters/numbers. Leave blank to auto-generate.
                            </p>
                        </div>
                    )}

                    <div className="flex gap-2 mt-3">
                        <Button variant="ghost" onClick={onClose} className="flex-1 text-white/70" disabled={busy}>Cancel</Button>
                        <Button
                            onClick={submit}
                            disabled={busy}
                            className="flex-1 font-black text-black"
                            style={{ background: THEME.accent }}
                            data-testid="tonk-create-submit"
                        >
                            {busy ? 'Dealing…' : 'DEAL ME IN'}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

// ─── House rules ───────────────────────────────────────────────────────
export const HouseRulesDialog = ({ open, onClose }) => (
    <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="bg-[#0B0B0B] text-white border max-h-[90vh] overflow-y-auto" style={{ borderColor: `${THEME.accent}55` }}>
            <DialogHeader>
                <div className="flex items-center justify-between gap-2">
                    <DialogTitle
                        className="text-white"
                        style={{ fontFamily: THEME.headingFont, letterSpacing: THEME.headingLetter }}
                    >
                        House Rules — Good Ol&apos; Tonk
                    </DialogTitle>
                    <button onClick={onClose} className="text-white/50 hover:text-white" aria-label="Close" data-testid="tonk-rules-close">
                        <X className="w-4 h-4" />
                    </button>
                </div>
            </DialogHeader>
            <div className="space-y-3 text-sm text-white/85 leading-snug">
                <RuleRow n="1" title="Ante up" body="Every seat pays the same ante into the pot. Fewest points at the end wins it." />
                <RuleRow n="2" title="5 cards each" body="Everyone gets dealt 5. Face cards are 10, ace is 1, others count their pip value." />
                <RuleRow n="3" title="Auto-Tonk on deal" body="Dealt exactly 49 or 50? Instant win, 2x the pot. 11 or lower? Instant win at 1x. (Rules toggleable by the host.)" />
                <RuleRow n="4" title="Draw, then discard" body="Each turn you MUST draw (from deck or discard) then discard exactly one card." />
                <RuleRow n="5" title="Spread & hit" body="Groups of 3+ same rank OR 3+ suited run can be laid face-up. You can hit others' spreads with a matching card." />
                <RuleRow n="6" title="Drop when low" body="Think you have the lowest total? Drop before your draw. If someone ties or beats you, they catch you and you pay 2x." />
                <RuleRow n="7" title="Empty hand = Tonk!" body="Get to zero cards (spreads + one final discard) and you tonk the whole table for 2x." />
                <RuleRow n="8" title="20-second turns" body="Slow players auto-drop when their timer runs out. Bots move quick — humans, don't sleep on it." />
            </div>
            <Button onClick={onClose} className="mt-3 font-black text-black w-full" style={{ background: THEME.accent }} data-testid="tonk-rules-got-it">
                Got it — deal me in
            </Button>
        </DialogContent>
    </Dialog>
);

const RuleRow = ({ n, title, body }) => (
    <div className="flex items-start gap-3">
        <div
            className="w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-black text-black shrink-0"
            style={{ background: THEME.accent }}
        >
            {n}
        </div>
        <div>
            <p className="font-bold text-white">{title}</p>
            <p className="text-white/70 text-[13px]">{body}</p>
        </div>
    </div>
);
